import type { z, ZodTypeAny } from "zod";
import { safeStorage, TOKEN_KEY } from "./storage";

export const API_BASE = "/api/v1";

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
    /** the short id of a server error ("7F3A2C"): riders quote it, admins find it in the problem list */
    public errorId: string | null = null,
    /** no answer within the time limit: never retried automatically (it would wait the limit again) */
    public timedOut = false,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

let lastError: string | null = null;
/** The id of the last server error this browser saw (sent with "Report a problem"). */
export function lastErrorId(): string | null {
  return lastError;
}

/** The browser got no answer (offline, the server unreachable, or no answer within `TIMEOUT_MS`): status 0. */
export const NO_CONNECTION = "No connection to Bason. Check your internet and try again.";
export const TIMED_OUT = "Bason didn't answer in time. Try again.";
/** How long to wait for the server to start answering. Slow calls pass their own (`timeoutMs`); the limit stops
 * once the answer has started, so reading a large body is never cut off. */
export const TIMEOUT_MS = 30_000;
export const SLOW_MS = 120_000; // AI chat (90 s server budget), route generation, plan rebuild, onboarding

export class ApiSchemaError extends Error {
  constructor(
    public path: string,
    public issues: string,
  ) {
    super(`Unexpected API response from ${path}: ${issues}`);
    this.name = "ApiSchemaError";
  }
}

type Listener = () => void;
const unauthorizedListeners = new Set<Listener>();
export function onUnauthorized(fn: Listener): () => void {
  unauthorizedListeners.add(fn);
  return () => unauthorizedListeners.delete(fn);
}

export function getToken(): string | null {
  return safeStorage.get(TOKEN_KEY);
}
export function setToken(token: string | null): void {
  if (token) safeStorage.set(TOKEN_KEY, token);
  else safeStorage.remove(TOKEN_KEY);
}

function query(params?: Record<string, string | number | undefined | null>): string {
  if (!params) return "";
  const q = new URLSearchParams();
  for (const [k, v] of Object.entries(params))
    if (v !== undefined && v !== null && v !== "") q.set(k, String(v));
  const s = q.toString();
  return s ? `?${s}` : "";
}

/** Names of fields as the rider sees them (validation messages). */
const FIELD_LABEL: Record<string, string> = {
  lthr: "Threshold heart rate",
  max_hr: "Max heart rate",
  resting_hr: "Resting heart rate",
  ftp_w: "FTP",
  bp_systolic: "Systolic pressure",
  bp_diastolic: "Diastolic pressure",
  bp_pulse: "Pulse",
  systolic: "Systolic pressure",
  diastolic: "Diastolic pressure",
  pulse: "Pulse",
  weight_kg: "Weight",
  event_date: "Race date",
  distance_km: "Distance",
  elevation_m: "Climbing",
  max_hours: "Riding hours",
  target_hours: "Hours",
  email: "E-mail",
  password: "Password",
  lat: "Latitude",
  lon: "Longitude",
};

type Issue = { loc?: unknown[]; msg?: string; type?: string; ctx?: Record<string, unknown> };

function fieldName(loc: unknown[] | undefined): string {
  const key = [...(loc ?? [])].reverse().find((x) => typeof x === "string" && x !== "body");
  if (typeof key !== "string") return "A value";
  if (FIELD_LABEL[key]) return FIELD_LABEL[key];
  const words = key.replace(/_/g, " ");
  return words.charAt(0).toUpperCase() + words.slice(1);
}

function issueText(i: Issue): string {
  const f = fieldName(i.loc);
  const c = i.ctx ?? {};
  switch (i.type) {
    case "less_than_equal":
      return `${f} must be ${String(c.le)} or less.`;
    case "less_than":
      return `${f} must be less than ${String(c.lt)}.`;
    case "greater_than_equal":
      return `${f} must be ${String(c.ge)} or more.`;
    case "greater_than":
      return `${f} must be more than ${String(c.gt)}.`;
    case "missing":
      return `${f} is missing.`;
    case "string_too_long":
      return `${f} is too long (at most ${String(c.max_length)} characters).`;
    case "string_too_short":
      return `${f} is too short.`;
    case "int_parsing":
    case "float_parsing":
    case "int_from_float":
      return `${f} must be a number.`;
    case "date_from_datetime_parsing":
    case "date_parsing":
      return `${f} must be a date.`;
    case "literal_error":
      return c.expected !== undefined ? `${f} must be one of ${String(c.expected)}.` : `${f} is not valid.`;
    case "too_short":
      return c.min_length !== undefined
        ? `${f} needs at least ${String(c.min_length)}.`
        : `${f} is too short.`;
    case "value_error": {
      // our own validators' sentence, without pydantic's "Value error, " prefix
      const own = String(i.msg ?? "")
        .replace(/^Value error, /, "")
        .trim();
      return own ? `${own.charAt(0).toUpperCase()}${own.slice(1).replace(/\.$/, "")}.` : `${f} is not valid.`;
    }
    default:
      return `${f} is not valid.`;
  }
}

/** FastAPI's 422 `detail` (a list of issues) as plain sentences, never raw JSON. */
export function validationText(detail: unknown): string {
  if (!Array.isArray(detail)) return "Some of the values are not valid. Check them and try again.";
  const lines = Array.from(new Set((detail as Issue[]).slice(0, 3).map(issueText)));
  return lines.join(" ") || "Some of the values are not valid. Check them and try again.";
}

interface RequestOpts {
  method?: string;
  params?: Record<string, string | number | undefined | null>;
  body?: unknown;
  form?: FormData;
  token?: string | null;
  /** ms until the server must start answering; `null` = no limit (uploads, downloads). Default `TIMEOUT_MS`. */
  timeoutMs?: number | null;
}

/** The CSRF cookie set at sign-in (readable on purpose): echoed in X-CSRF-Token on every write made
 * with the session cookie, which another site can't do. */
export function csrfToken(): string | null {
  for (const part of document.cookie.split(";")) {
    const [k, ...v] = part.trim().split("=");
    if (k === "__Host-amr_csrf" || k === "amr_csrf") return decodeURIComponent(v.join("="));
  }
  return null;
}

async function raw(path: string, opts: RequestOpts = {}): Promise<Response> {
  const headers: Record<string, string> = {};
  const token = opts.token !== undefined ? opts.token : getToken();
  if (token) headers.Authorization = `Bearer ${token}`;
  const method = opts.method ?? (opts.body !== undefined || opts.form ? "POST" : "GET");
  if (!token && method !== "GET") {
    const csrf = csrfToken();
    if (csrf) headers["X-CSRF-Token"] = csrf;
  }
  let body: BodyInit | undefined;
  if (opts.form) body = opts.form;
  else if (opts.body !== undefined) {
    headers["Content-Type"] = "application/json";
    body = JSON.stringify(opts.body);
  }
  // own timer (not AbortSignal.timeout: iOS 15 lacks it), cleared once the answer starts (G3 review)
  const limit = opts.timeoutMs !== undefined ? opts.timeoutMs : opts.form ? null : TIMEOUT_MS;
  const ctl = limit != null ? new AbortController() : null;
  const timer = ctl ? setTimeout(() => ctl.abort(), limit!) : undefined;
  let res: Response;
  try {
    res = await fetch(`${API_BASE}${path}${query(opts.params)}`, {
      method,
      headers,
      body,
      credentials: "same-origin",
      signal: ctl?.signal,
    });
  } catch {
    // fetch rejects only when there is no HTTP answer: never show "Failed to fetch" (G3)
    throw ctl?.signal.aborted ? new ApiError(0, TIMED_OUT, null, true) : new ApiError(0, NO_CONNECTION);
  } finally {
    clearTimeout(timer);
  }
  if (res.status === 401) {
    unauthorizedListeners.forEach((fn) => fn());
    throw new ApiError(401, "Please sign in again");
  }
  if (!res.ok) {
    let detail = `${res.status} ${res.statusText}`;
    let errorId = res.headers.get("X-Error-Id");
    try {
      const j = (await res.json()) as { detail?: unknown; error_id?: unknown };
      if (typeof j.detail === "string") detail = j.detail;
      else if (j.detail) detail = validationText(j.detail);
      if (typeof j.error_id === "string") errorId = j.error_id;
    } catch {
      /* non-JSON error */
    }
    if (errorId) {
      lastError = errorId;
      if (!detail.includes(errorId)) detail = `${detail} (error ${errorId})`;
    }
    throw new ApiError(res.status, detail, errorId);
  }
  return res;
}

/** JSON request validated against a zod schema. */
export async function api<S extends ZodTypeAny>(
  path: string,
  schema: S,
  opts: RequestOpts = {},
): Promise<z.output<S>> {
  const res = await raw(path, opts);
  let data: unknown = null;
  if (res.status !== 204) {
    try {
      data = await res.json();
    } catch {
      // a cut-off or non-JSON body (a proxy page): never the parser's text; a server-side problem, retried (G3)
      throw new ApiError(502, "Bason sent an answer the app couldn't read. Try again.");
    }
  }
  const parsed = schema.safeParse(data);
  if (!parsed.success) {
    const issues = parsed.error.issues
      .slice(0, 3)
      .map((i) => `${i.path.join(".")}: ${i.message}`)
      .join("; ");
    console.error(`[api] schema mismatch for ${path}`, parsed.error.issues, data);
    throw new ApiSchemaError(path, issues);
  }
  return parsed.data as z.output<S>;
}

/** Request without a response body (204). */
export async function apiVoid(path: string, opts: RequestOpts = {}): Promise<void> {
  await raw(path, opts);
}

/** Authenticated file download → browser save dialog. */
export async function download(path: string, fallbackName: string): Promise<void> {
  const res = await raw(path, { timeoutMs: null }); // the export zip is built before it streams
  const blob = await res.blob();
  const cd = res.headers.get("Content-Disposition") ?? "";
  const m = /filename="?([^";]+)"?/.exec(cd);
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = m?.[1] ?? fallbackName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/** After sign-in: a bootstrap (setup) token is exchanged for a stored personal token and retired
 * server-side. Returns the token to keep (the new one, or the one given). */
export async function exchangeToken(token: string): Promise<string> {
  try {
    const res = await fetch(`${API_BASE}/auth/exchange`, {
      method: "POST",
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) return token;
    const j = (await res.json()) as { token?: string | null };
    return j.token || token;
  } catch {
    return token;
  }
}

/** Only http(s) links are ever rendered from data (no javascript:/data: URLs). */
export function safeUrl(u: string | null | undefined): string | undefined {
  if (!u) return undefined;
  try {
    const p = new URL(u);
    return p.protocol === "https:" || p.protocol === "http:" ? p.href : undefined;
  } catch {
    return undefined;
  }
}

/** Check a token without storing it (used by the token gate). */
export async function verifyToken(token: string): Promise<boolean> {
  try {
    const res = await fetch(`${API_BASE}/me`, { headers: { Authorization: `Bearer ${token}` } });
    return res.ok;
  } catch {
    return false;
  }
}
